<template>
    <div class="ma-4">
        <v-snackbar
            v-model="msg"
            top
            :color="snackbarColor"
            :timeout="3000"
            elevation="4"
            class="rounded-lg"
        >
            <div class="d-flex align-center">
                <v-icon class="mr-2">{{
                    snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
                }}</v-icon>
                {{ text }}
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn icon small v-bind="attrs" @click="msg = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>

        <v-card class="rounded-lg">
            <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
                <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
                    <v-icon>navigate_before</v-icon>
                </v-btn>

                <span class="text-h6 font-weight-medium grey--text text--darken-3">
                    Expiring Annual Documents Report
                </span>

                <v-spacer></v-spacer>

                <v-btn
                    fab
                    small
                    @click="printTable()"
                    class="mr-2 white--text"
                    color="primary"
                    elevation="2"
                >
                    <v-icon>print</v-icon>
                </v-btn>
            </v-app-bar>

            <v-divider></v-divider>

            <v-card-text class="pa-6 grey lighten-5">
                <v-row dense>
                    <v-col cols="12" md="2">
                        <v-menu
                            ref="calendarMenu"
                            v-model="calendarMenu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="filters.startDate"
                                    label="Start Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="filters.startDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                @change="documentQuery"
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendarMenu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendarMenu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-menu
                            ref="calendar2Menu"
                            v-model="calendar2Menu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="filters.endDate"
                                    label="End Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="filters.endDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                @change="documentQuery"
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendar2Menu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendar2Menu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-select
                            label="Client Status"
                            :items="clientStatuses"
                            v-model="filters.clientStatus"
                            clearable
                            @change="documentQuery()"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-select
                            label="Select a Client"
                            :items="filteredClients"
                            item-text="Name"
                            item-value="._id"
                            v-model="filters.ClientID"
                            clearable
                            @change="documentQuery"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-select
                            v-model="filters.newTypeQuery"
                            label="Document Type"
                            :items="type"
                            :readonly="readonly"
                            clearable
                            @change="documentQuery()"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-btn
                            color="primary"
                            block
                            height="40"
                            class="rounded-lg"
                            :loading="isLoading"
                            elevation="1"
                            @click="documentQuery()"
                        >
                            <v-icon left>mdi-magnify</v-icon>
                            Search
                        </v-btn>
                    </v-col>
                </v-row>

                <v-row dense class="mt-4">
                    <v-col cols="12" md="3" v-if="filters.newTypeQuery == 'Mandatory'">
                        <v-select
                            label="Mandatory"
                            v-model="filters.newMandatoryQuery"
                            :items="mandatory"
                            :readonly="readonly"
                            clearable
                            @change="documentQuery()"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="3" v-if="filters.newTypeQuery == 'Elective'">
                        <v-select
                            label="Elective"
                            v-model="filters.newElectiveQuery"
                            :items="elective"
                            :readonly="readonly"
                            clearable
                            @change="documentQuery()"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>
                </v-row>
            </v-card-text>

            <!-- Data Table -->
            <v-card-text class="pa-6">
                <v-data-table
                    :headers="headers"
                    :items="documents"
                    sort-by="DateExpires"
                    sort-asc
                    class="elevation-1 rounded-lg"
                    :loading="isLoading"
                    loading-text="Loading Annual Documents"
                    :page="page"
                    :pageCount="pageCount"
                    :server-items-length="itemCount"
                    :options.sync="options"
                    :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50],
                        showFirstLastPage: true,
                        'items-per-page-text': 'Rows per page:',
                        class: 'rounded-lg',
                    }"
                    @update:options="documentQuery"
                >
                    <template v-slot:item.select="{ item }">
                        <v-btn
                            x-small
                            color="primary"
                            @click="viewDoc(item)"
                            class="px-3"
                        >
                            <v-icon left x-small>mdi-eye</v-icon>
                            View
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="dialog" max-width="90%" class="rounded-lg">
            <v-card class="rounded-lg">
                <v-card-title class="primary white--text">
                    <span class="text-h6">{{ titleForm }}</span>
                </v-card-title>
                <v-card-text class="pt-6">
                    <v-container>
                        <v-row>
                            <v-col cols="12" sm="12" md="4">
                                <v-select
                                    v-model="newType"
                                    label="Document Type"
                                    :items="type"
                                    :readonly="readonly"
                                    outlined
                                    dense
                                    class="rounded-lg"
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="12" md="4" v-if="newType == 'Mandatory'">
                                <v-select
                                    label="Mandatory"
                                    v-model="newMandatory"
                                    :items="mandatory"
                                    :readonly="readonly"
                                    outlined
                                    dense
                                    class="rounded-lg"
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="12" md="4" v-if="newType == 'Elective'">
                                <v-select
                                    label="Elective"
                                    v-model="newElective"
                                    :items="elective"
                                    :readonly="readonly"
                                    outlined
                                    dense
                                    class="rounded-lg"
                                ></v-select>
                            </v-col>
                            <v-col class="d-flex" cols="12" sm="12">
                                <v-textarea
                                    v-model="newFocus"
                                    label="Focus"
                                    outlined
                                    :readonly="readonly"
                                    class="rounded-lg"
                                ></v-textarea>
                            </v-col>
                            <v-col class="d-flex" cols="12" sm="12">
                                <v-textarea
                                    v-model="newDetails"
                                    label="Details"
                                    outlined
                                    :readonly="readonly"
                                    class="rounded-lg"
                                ></v-textarea>
                            </v-col>
                            <v-col cols="12" sm="12" md="4">
                                <v-menu
                                    ref="menu"
                                    v-model="menu"
                                    :close-on-content-click="false"
                                    :return-value.sync="date"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="newDateIssued"
                                            label="Date Issued"
                                            prepend-icon="mdi-calendar"
                                            :readonly="readonly"
                                            v-bind="attrs"
                                            v-on="on"
                                            outlined
                                            dense
                                            class="rounded-lg"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="newDateIssued"
                                        no-title
                                        scrollable
                                        :readonly="readonly"
                                        class="rounded-lg"
                                    >
                                        <v-spacer></v-spacer>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="menu = false"
                                        >
                                            Cancel
                                        </v-btn>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="$refs.menu.save(date)"
                                        >
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" sm="12" md="4">
                                <v-menu
                                    ref="menu2"
                                    v-model="menu2"
                                    :close-on-content-click="false"
                                    :return-value.sync="date"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="newDateRequested"
                                            label="Date Requested"
                                            prepend-icon="mdi-calendar"
                                            :readonly="readonly"
                                            v-bind="attrs"
                                            v-on="on"
                                            outlined
                                            dense
                                            class="rounded-lg"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="newDateRequested"
                                        no-title
                                        scrollable
                                        :readonly="readonly"
                                        class="rounded-lg"
                                    >
                                        <v-spacer></v-spacer>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="menu2 = false"
                                        >
                                            Cancel
                                        </v-btn>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="$refs.menu2.save(date)"
                                        >
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" sm="12" md="4">
                                <v-menu
                                    ref="menu3"
                                    v-model="menu3"
                                    :close-on-content-click="false"
                                    :return-value.sync="date"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="newDateExpires"
                                            label="Date Expires"
                                            prepend-icon="mdi-calendar"
                                            :readonly="readonly"
                                            v-bind="attrs"
                                            v-on="on"
                                            outlined
                                            dense
                                            class="rounded-lg"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="newDateExpires"
                                        no-title
                                        scrollable
                                        :readonly="readonly"
                                        class="rounded-lg"
                                    >
                                        <v-spacer></v-spacer>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="menu3 = false"
                                        >
                                            Cancel
                                        </v-btn>
                                        <v-btn
                                            text
                                            color="primary"
                                            @click="$refs.menu3.save(date)"
                                        >
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col class="d-flex" cols="12" sm="12">
                                <v-textarea
                                    v-model="newNotes"
                                    label="Notes"
                                    outlined
                                    :readonly="readonly"
                                    class="rounded-lg"
                                ></v-textarea>
                            </v-col>
                            <v-col cols="12" sm="12" md="6">
                                <form enctype="multipart/form-data">
                                    <v-file-input
                                        v-model="newDocuments"
                                        class="pa-2 rounded-lg"
                                        label="Upload Document"
                                        filled
                                        multiple
                                        prepend-icon="mdi-camera"
                                        accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        @change="imgSelected"
                                        :readonly="readonly"
                                    >
                                        <template #selection="{ text, index }">
                                            <v-chip small label close color="primary" @click:close="deleteChip(index)">
                                                {{ text }}
                                            </v-chip>
                                        </template>
                                    </v-file-input>
                                </form>
                            </v-col>
                            <v-carousel v-if="imageDetails.length > 0">
                                <template v-for="(item, index) in imageDetails">
                                    <v-carousel-item
                                        :key="index"
                                    >
                                        <v-row class="flex-nowrap" style="height:100%">
                                            <v-img :src="item.AWSLink">
                                                <v-row class="fill-height"
                                                       align="center"
                                                       justify="center"
                                                >
                                                    <div style="color: #000;padding-top: 30%;">{{ item.Name }}</div>
                                                </v-row>

                                            </v-img>

                                        </v-row>
                                    </v-carousel-item>
                                </template>
                            </v-carousel>
                            <template v-if="pdfDetails.length > 0">
                                <v-col cols="12" sm="12" md="12">
                                    <div v-for="(item, i) in pdfDetails"
                                         :key="i"
                                    >
                                        <LoadPDF
                                            :pdfFile="item.aws"
                                            :id="item.id"
                                        />
                                    </div>
                                </v-col>
                            </template>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        @click="updateDoc()"
                        v-if="editDoc"
                        elevation="1"
                    >
                        Update
                    </v-btn>
                    <v-btn
                        color="grey"
                        text
                        @click="close"
                    >
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <div id="print">
            <report-header>
                <h2>Expiring Annual Documents Report</h2>
            </report-header>
            <v-row>
                <v-col cols="12">
                    <v-data-table
                        :headers="headers"
                        :items="documents"
                        sort-by="DateExpires"
                        sort-asc
                        class="elevation-1"
                        :loading="isLoading"
                        loading-text="Loading Annual Documents"
                        :items-per-page="-1"

                    >
                        <template v-slot:item.select="{ item }">

                            <v-btn
                                x-small
                                class="white--text"
                                color="#2b3990"
                                @click="viewDoc(item)"
                            >
                                View
                            </v-btn>

                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </div>
    </div>
</template>
<script>
import axios from 'axios'
// import moment from 'moment'
// import Foot from '../components/Footer'
import LoadPDF from '../../components/LoadPDF.vue'
import {STATUS_ARRAY} from '../../enums/UserStatusEnum';
import ReportHeader from '@/components/Report/ReportHeader.vue';
import {DateTime} from 'luxon';
import {SORT_ORDER} from '../../constants/MongoDBConstants.js';


const url = require("../../client_config/config").url;

export default {
    components: {
        LoadPDF,
        ReportHeader
    },
    data: () => ({
        headers: [
            {
                text: 'Client',
                align: 'start',
                sortable: true,
                value: 'ClientInfo.Name',
            },
            {text: "Expiration Date", value: "DateExpires"},
            {text: "Type", value: "Type"},
            {text: "Mandatory", value: "Mandatory"},
            {text: "Elective", value: "Elective"},
            {text: "Focus", value: "Focus"},
            {text: "Notes", value: "Notes"},
            {text: "", value: "select"},
            // { text: "Actions", value: "actions", sortable: false },
        ],
        date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
        clients: [],
        documents: [],
        calendarMenu: false,
        calendar2Menu: false,
        report: '',
        clientName: '',
        isLoading: false,
        programs: [],
        userState: localStorage.getItem('userState'),
        menu: false,
        menu2: false,
        menu3: false,
        dialog: false,
        editDoc: false,
        newDoc: false,
        msg: false,
        mandatoryPicked: false,
        electivePicked: false,
        text: "",
        docs: [],
        tempData: [],
        type: ["Mandatory", "Elective"],
        mandatory: [
            "Annual PCP",
            "Annual Skills Assessment",
            "Annual Physical Exam",
            "Authorization for Emergency Medical Aid",
            "Authorization to Disclose Health Information",
            "Authorization for Medication Administration by DSS",
            "Auth for Restrictive Interventions",
            "Behavior Assessment",
            "Behavior Support Plan",
            "Consent for Med Administration",
            "Consent for Rights Restrictions",
            "Consent to Receive Services",
            "Consents and Authorizations",
            "DSPD RElease of Information",
            "DWS Authorization to Disclose Medical Eligibility",
            "DWS Authorization to Release Information",
            "DWS Authorization to Release Information to Third Party",
            "Human Righs - HRC", "Human Rights and Grievance Procedure",
            "HRC/BIC Notes",
            "MAAT",
            "Medical Clearance",
            "Medical Treatement Consent Form",
            "My Vision/Support Plan Signature Page",
            "OTC Standing Orders",
            "PCP Signature Sheet",
            "PCSP", "Release of Non-Medical Information",
            "Request and Authorization for Tests and Medical Records",
            "Responsibilities Agreement",
            "Risk and Support Screener",
            "RHS Provider Consents",
            "Service Authorization",
            "Service Plan",
            "Social Assessment",
            "Support Strategies",
            "Statement of Choice",
            "Voc Rehab",
            "Voter Registration",
            "Waiver Level of Care Determination",
            "Wellsky Facesheet",
        ],
        elective: [
            "Abuse/Neglect/Exploitation",
            "Asipiration/Choking",
            "Behavior Intervention",
            "Compromised Environment",
            "Debit Card",
            "Drug/Alcohol",
            "EBT Card",
            "Fatality",
            "Hospital",
            "Injury",
            "Immunizations",
            "Medication Errors",
            "Missing Person",
            "Property Destruction",
            "Seizure",
            "Suicide Attempt/Threat",
            "Theft",
            "Other (Describe)"
        ],
        titleForm: "New Annual Document",
        docID: "",
        newType: "",
        newMandatory: "",
        newElective: "",
        newFocus: "",
        newDetails: "",
        newDateIssued: "",
        newDateRequested: "",
        newDateExpires: "",
        newNotes: "",
        imageDetails: [],
        pdfDetails: [],
        newDocuments: [],
        newDocumentsImage: "",
        uploadImg: "",
        readonly: false,
        directCare: false,
        selectedProgram: '',
        requiredField: [
            v => !!v || 'This is required',
        ],
        page: 1,
        pageCount: 1,
        itemCount: 0,
        options: {},
        clientStatus: "",
        clientStatuses: STATUS_ARRAY,
        filteredClients: [],
        filters: {
            newTypeQuery: "",
            startDate: "",
            endDate: "",
            ClientID: '',
            newMandatoryQuery: "",
            newElectiveQuery: "",
        },
        searchQuery: "",
        searchTimeout: null,
        snackbarColor: "success",
    }),

    computed: {
        queryParams(){
            let {page, sortBy, sortDesc, itemsPerPage} = this.options;
            const defaultEndDate = DateTime.now().endOf('month').toFormat('yyyy-MM-dd');
            const defaultStartDate = DateTime.now().startOf('month').toFormat('yyyy-MM-dd');
            const sortField = Array.isArray(sortBy) ? (sortBy[0] ?? 'DateExpires') : 'DateExpires';
            const sortDirection = Array.isArray(sortDesc) ? (sortDesc[0] ? SORT_ORDER.DESC:SORT_ORDER.ASC) : SORT_ORDER.DESC;

           return { 
                startDate: this.filters.startDate || defaultStartDate,
                endDate: this.filters.endDate || defaultEndDate,
                clientId: this.filters.ClientID || '',
                state: this.userState || '',
                ClientStatus: this.filters.clientStatus || '',
                Mandatory: this.filters.newMandatoryQuery || '',
                Elective: this.filters.newElectiveQuery || '',
                Type: this.filters.newTypeQuery || '',
                page: page,
                limit: itemsPerPage || 10,
                sortField: sortField,
                sortDirection: sortDirection,
                search: this.searchQuery || ''
            }
        }
    },

    created() {
        this.getClients();
        this.getPrograms();
        this.getFilters();
        // this.documentQuery();
    },

    watch: {
        options: {
            handler() {
                this.documentQuery();
            },
            deep: true
        },

        clientStatus: function (val) {
            if (val == '' || val == null) this.filteredClients = this.clients;
            else this.filteredClients = this.clients.filter((client) => client.Status == val);
        },

        filters: {
            handler() {
                this.setFilters();
            },
            deep: true
        }

    },
    methods: {
        initialize() {
            this.clients = [];
        },

        async getClients() {
            let endpoint = `${url}/clients`;

            await axios.get(endpoint)
                .then((res) => {
                    this.clients = res.data.clients;
                    this.filteredClients = this.clients;
                });
        },

        async getPrograms() {
            await axios.get(`${url}/units/state/${this.userState}`)
                .then((res) => {
                    this.programs = res.data.units;
                });
        },

        clear() {
            this.newType = "";
            this.newMandatory = "";
            this.newElective = "";
            this.newFocus = "";
            this.newDetails = "";
            this.newDateIssued = "";
            this.newDateRequested = "";
            this.newDateExpires = "";
            this.newNotes = "";
            this.newDocuments = [];
            this.newDocumentsImage = "";
            this.newStatus = "";
        },

        close() {
            this.dialog = false
            this.clear()
        },

        async updateDoc() {
            var req_body = {
                Type: this.newType,
                Mandatory: this.newMandatory,
                Elective: this.newElective,
                Focus: this.newFocus,
                Details: this.newDetails,
                DateIssued: this.newDateIssued,
                DateRequested: this.newDateRequested,
                DateExpires: this.newDateExpires,
                Notes: this.newNotes,
                Status: this.newStatus,
            };
            await axios.put(`${url}/annualDocs/${this.docID}`, req_body).then((response) => {
                this.documentQuery();
                if (response.status === 400) {
                    response.json().then(function (data) {
                        console.log(data.msg);
                    });
                }
                this.msg = true;
                this.text = "The annual document has been updated successfully.";

            });

            await this.saveImg();

            this.isLoading = false;
            this.close();
        },

        viewDoc(item) {
            this.titleForm = "Viewing Annual Document";
            this.dialog = true;
            this.editDoc = true;
            this.newDoc = false;
            this.newType = item.Type;
            this.newMandatory = item.Mandatory;
            this.newElective = item.Elective;
            this.newFocus = item.Focus;
            this.newDetails = item.Details;
            this.newDateIssued = item.DateIssued;
            this.newDateRequested = item.DateRequested;
            this.newDateExpires = item.DateExpires;
            if (item.DateExpires === "Invalid date") {
                this.newDateExpires = "";
            }
            this.newNotes = item.Notes;

            if (item.documents.length > 0) {
                const tempData = [];
                for (let i = 0; i < item.documents.length; i++) {
                    tempData.push({
                        name: item.documents[i].Name,
                    });
                    let fileExt = item.documents[i].AWSLink.split('.').pop();
                    if (fileExt == "pdf") {
                        this.pdfDetails.push(
                            {
                                name: item.documents[i].Name,
                                id: item.documents[i]._id,
                                aws: item.documents[i].AWSLink
                            }
                        );
                    } else {
                        this.imageDetails.push(item.documents[i]);
                    }
                }

                this.newDocuments = tempData;

            }

            this.docID = item._id;
        },


        async documentQuery() {
            this.documents = [];
            this.isLoading = true;
            const params = this.queryParams;
            
            await axios.get(`${url}/annualDocs/expiring/docs`, {params}).then(res => {
                const {docs, totalPages, totalDocs} = res.data;
                this.documents = docs;
                this.pageCount = totalPages;
                this.itemCount = totalDocs;
                this.isLoading = false;
            })
        },

        onSearchInput() {
            // Debounce search input to avoid too many API calls
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.documentQuery();
            }, 500);
        },

        getClientID(item) {
            this.client = item.ClientID;
            window.localStorage.setItem('clientId', this.client)
        },

        printTable() {
            window.print()

        },

        imgSelected(e) {
            this.uploadImg = this.renameFileHelper(e, this.clientName, "AnnualDoc");
        },
        saveImg() {
            this.message = "";
            for (let i = 0; i < this.uploadImg.length; i++) {
                this.uploadFiles(this.uploadImg[i]);
            }
        },

        uploadFiles(file) {
            const formData = new FormData();
            formData.append('annualDoc-img', file);
            var ct = {headers: {"content-type": "multipart/form-data"}};
            axios.post(`${url}/annualDocs/imguploader/${this.docID}/${this.clientID}`, formData, ct)
                .then(res => {
                    if (res) {
                        this.getDocs()
                    }
                })
                .catch(err => {
                    console.log(err);
                });
        },

        deleteChip(index) {
            // Prompt here with text if required
            this.newDocuments.splice(index, 1)
            //this.files = this.previousFiles
        },

        getFilters() {
            let filters = localStorage.getItem('annualDocumentsReportFilter');
            if (filters) {
                this.filters = JSON.parse(filters);
            }
        },

        setFilters() {
            localStorage.setItem('annualDocumentsReportFilter', JSON.stringify(this.filters));
        }

    }
}
</script>
<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}
</style>

<style>
#print {
    display: none
}

@media only screen and (min-width: 768px) {
    .mobile {
        display: none;
    }
}

@media only screen and (max-width: 600px) {
    .desktop {
        display: none;
    }

}


@media only screen and (min-width: 768px) {
    .mobile {
        display: none;
    }
}

@media only screen and (max-width: 600px) {
    .desktop {
        display: none;
    }

}

@media print {
    #print {
        display: block;
        border: none;
        width: 1200px;
        padding-right: 300px;
        margin-left: -200px;

    }


    nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
        display: none;
        border: none;

    }

    @page {
        margin: 0;
    }

    body {
        margin: 1.6cm;
    }


}
</style>
